'use client';

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner, Alert, Badge, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUserDoctor,
  faAward,
  faBriefcase,
  faCalendar,
  faLocationDot,
  faFileAlt,
  faDownload,
  faTimes,
  faEye,
  faCertificate,
  faCheckCircle,
  faTimesCircle,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { getLocumById, toggleLocumStatus, getLocumBookings } from 'features/admin/adminApi';
import { downloadDocument, viewDocument } from 'features/profile/profileApi';
import Button from 'components/base/Button';

const AdminLocumDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [locum, setLocum] = useState<any>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // FETCH LOCUM DETAILS (includes documents)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const locumData = await getLocumById(id || '');
        setLocum(locumData);

        // Fetch bookings
        try {
          setBookingsLoading(true);
          const bookingsData = await getLocumBookings(id || '');
          setBookings(bookingsData);
        } catch (bookingErr: any) {
          console.error('Error fetching bookings:', bookingErr.message);
        } finally {
          setBookingsLoading(false);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // TOGGLE STATUS
  const handleToggleStatus = async () => {
    try {
      await toggleLocumStatus(id || '');
      const updatedLocum = await getLocumById(id || '');
      setLocum(updatedLocum);
      setSuccess('Status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // HANDLE DOCUMENT DOWNLOAD
  const handleDocumentDownload = async (document: any) => {
    try {
      await downloadDocument(document.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    }
  };

  // HANDLE DOCUMENT VIEW
  const handleDocumentView = (document: any) => {
    viewDocument(document.document_name);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <Row className="py-5">
          <Col className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-body-tertiary">Loading locum details...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!locum) {
    return (
      <Container className="py-5">
        <Row className="py-5">
          <Col className="text-center">
            <Alert variant="danger">Locum not found</Alert>
            <Button 
              variant="phoenix-primary" 
              onClick={() => navigate('/dashboard/admin/user-management/manage-locum')}
            >
              Back to Manage Locums
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-2 py-lg-4">
      {/* Alerts */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}
      {success && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success" onClose={() => setSuccess('')} dismissible>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3 mb-3">
            <Button
              variant="phoenix-secondary"
              size="sm"
              onClick={() => navigate('/dashboard/admin/user-management/manage-locum')}
              startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              Back
            </Button>
          </div>
          <div className="d-flex align-items-center gap-3">
            <h2 className="text-body-emphasis mb-0">
              <FontAwesomeIcon icon={faUserDoctor} className="me-2 text-primary" />
              {locum.user.name}
            </h2>
            <Badge className={`rounded-pill ${locum.is_active ? 'bg-success' : 'bg-danger'}`}>
              {locum.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <p className="text-body-tertiary mt-2 mb-0">
            Admin Workspace • Locum Details
          </p>
        </Col>
      </Row>

      {/* Personal Information Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">Personal Information</Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                {/* Email */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Email
                  </p>
                  <a
                    href={`mailto:${locum.user.email}`}
                    className="text-primary text-decoration-none fw-semibold"
                  >
                    {locum.user.email}
                  </a>
                </Col>

                {/* Phone */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Phone
                  </p>
                  <a
                    href={`tel:${locum.profile.phone}`}
                    className="text-primary text-decoration-none fw-semibold"
                  >
                    {locum.profile.phone}
                  </a>
                </Col>

                {/* Location */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Location
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="me-2 text-danger"
                    />
                    {locum.profile.location}
                  </h6>
                </Col>

                {/* Travel Distance */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Travel Distance
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    {locum.profile.travel_distance} KM
                  </h6>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Professional Details Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
                Professional Details
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                {/* Qualifications */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Qualification
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faAward}
                      className="me-2 text-warning"
                    />
                    {locum.profile.qualifications}
                  </h6>
                </Col>

                {/* Role */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Locum Role
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faBriefcase}
                      className="me-2 text-primary"
                    />
                    {locum.profile.locum_role === 'pharmacist'
                      ? 'Pharmacist'
                      : 'Technician'}
                  </h6>
                </Col>

                {/* Experience */}
                <Col md={6}>
                  <p className="text-body-tertiary small mb-2 text-uppercase fw-bold">
                    Experience
                  </p>
                  <h6 className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon
                      icon={faCalendar}
                      className="me-2 text-info"
                    />
                    {locum.profile.experience_years} Years
                  </h6>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Documents Card - SAME AS PROFILE PAGE */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                Documents ({locum.documents?.length || 0})
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {locum.documents && locum.documents.length > 0 ? (
                <div className="list-group">
                  {locum.documents.map((doc: any) => (
                    <div
                      key={doc.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <h6 className="mb-1 d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={faCertificate}
                            className="me-2 text-warning"
                          />
                          {doc.doc_type.replace(/_/g, ' ').toUpperCase()}
                        </h6>
                        <small className="text-body-secondary">
                          {doc.document_name}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="phoenix-secondary"
                          size="sm"
                          startIcon={<FontAwesomeIcon icon={faEye} />}
                          onClick={() => handleDocumentView(doc)}
                        >
                          View
                        </Button>
                        <Button
                          variant="phoenix-primary"
                          size="sm"
                          startIcon={<FontAwesomeIcon icon={faDownload} />}
                          onClick={() => handleDocumentDownload(doc)}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No documents uploaded yet
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Bookings Card */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <FontAwesomeIcon icon={faBriefcase} className="me-2 text-primary" />
                Booking History ({bookings?.length || 0})
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {bookingsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <p className="text-body-tertiary">Loading bookings...</p>
                </div>
              ) : bookings && bookings.length > 0 ? (
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead className="bg-body-tertiary">
                      <tr>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Pharmacy
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Date
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Time
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3">
                          Location
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3 text-center">
                          Pay Rate
                        </th>
                        <th className="text-body-emphasis fw-bold text-uppercase fs-10 ps-4 pe-4 py-3 text-center">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking: any) => (
                        <tr key={booking.booking_id} className="border-bottom border-translucent">
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body-emphasis fw-semibold">
                              {booking.pharmacy_name || 'N/A'}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body">
                              <FontAwesomeIcon icon={faCalendar} className="me-2 text-info" />
                              {new Date(booking.date).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body">
                              <FontAwesomeIcon icon={faClock} className="me-2 text-warning" />
                              {booking.start_time} - {booking.end_time}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3">
                            <span className="text-body-emphasis fw-semibold">
                              <FontAwesomeIcon
                                icon={faLocationDot}
                                className="me-2 text-danger"
                              />
                              {booking.shift_location}
                            </span>
                          </td>
                          <td className="ps-4 pe-4 py-3 text-center">
                            <Badge bg="success" className="rounded-pill">
                              £{booking.pay_rate}
                            </Badge>
                          </td>
                          <td className="ps-4 pe-4 py-3 text-center">
                            <Badge
                              className={`rounded-pill ${
                                booking.status === 'confirmed'
                                  ? 'bg-info'
                                  : booking.status === 'completed'
                                  ? 'bg-success'
                                  : 'bg-danger'
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={
                                  booking.status === 'confirmed'
                                    ? faClock
                                    : booking.status === 'completed'
                                    ? faCheckCircle
                                    : faTimesCircle
                                }
                                className="me-1"
                              />
                              {booking.status.charAt(0).toUpperCase() +
                                booking.status.slice(1)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No bookings found
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actions */}
      <Row>
        <Col>
          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant={locum.is_active ? 'danger' : 'success'}
              onClick={handleToggleStatus}
            >
              {locum.is_active ? 'Deactivate' : 'Activate'} Locum
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminLocumDetailsPage;
